import { useMappingStackStore } from '@/store/mappingStackStore';

export const MAPPING_URL_PARAM = 'mapping';

type MappingUrlState = {
  stack: any[];
  currentIndex: number;
  tableStateByLevelId: Record<string, any>;
};

function normalizeSearchQuery(search: string): string {
  if (!search) return '';
  return search.startsWith('?') ? search : `?${search}`;
}

export function stripMappingParam(search: string): string {
  const normalized = normalizeSearchQuery(search);
  if (!normalized) return '';
  const params = new URLSearchParams(normalized.slice(1));
  if (!params.has(MAPPING_URL_PARAM)) return normalized;
  params.delete(MAPPING_URL_PARAM);
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function searchHasMapping(search: string): boolean {
  const normalized = normalizeSearchQuery(search);
  if (!normalized) return false;
  return new URLSearchParams(normalized.slice(1)).has(MAPPING_URL_PARAM);
}

export function clearMappingParamFromLocation(): void {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search);
  if (!params.has(MAPPING_URL_PARAM)) return;
  params.delete(MAPPING_URL_PARAM);
  const query = params.toString();
  window.history.replaceState(
    window.history.state,
    '',
    `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
  );
}

function toBase64Url(value: string): string {
  const utf8 = encodeURIComponent(value).replace(/%([0-9A-F]{2})/g, (_, p1) =>
    String.fromCharCode(parseInt(p1, 16)),
  );
  return btoa(utf8).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(value: string): string {
  const padded = value
    .replace(/-/g, '+')
    .replace(/_/g, '/')
    .padEnd(Math.ceil(value.length / 4) * 4, '=');
  const binary = atob(padded);
  const percentEncoded = Array.from(binary)
    .map(c => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`)
    .join('');
  return decodeURIComponent(percentEncoded);
}

function sanitizeStackForUrl(stack: any[]): any[] {
  return (stack ?? []).map(level => ({
    ...level,
    labelNode: undefined,
  }));
}

export function encodeMappingStateForUrl(state: MappingUrlState): string {
  const serializable: MappingUrlState = {
    stack: sanitizeStackForUrl(state.stack),
    currentIndex: state.currentIndex ?? 0,
    tableStateByLevelId: state.tableStateByLevelId ?? {},
  };
  return toBase64Url(JSON.stringify(serializable));
}

export function decodeMappingStateFromUrl(value: string): MappingUrlState | null {
  try {
    const parsed = JSON.parse(fromBase64Url(value));
    if (!parsed || !Array.isArray(parsed.stack)) return null;

    return parsed;
  } catch {
    return null;
  }
}

export function applyMappingStateFromSearch(search: string): void {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  const params = new URLSearchParams(raw);
  const encoded = params.get(MAPPING_URL_PARAM);

  if (!encoded) {
    useMappingStackStore.getState().collapseToRootLevel();
    return;
  }

  const decoded = decodeMappingStateFromUrl(encoded);
  if (!decoded || decoded.stack.length === 0) {
    useMappingStackStore.getState().collapseToRootLevel();
    return;
  }

  useMappingStackStore.setState({
    stack: decoded.stack,
    currentIndex: Math.max(0, Math.min(decoded.currentIndex ?? 0, decoded.stack.length - 1)),
    tableStateByLevelId: decoded.tableStateByLevelId ?? {},
  });
}
